/* import __COLOCATED_TEMPLATE__ from './ecommerce-funnel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import type { Options, SeriesOptionsType, SeriesSankeyOptions, Point } from 'highcharts';
import { use } from 'ember-resources/util/function-resource';
import PALETTE from '@intercom/pulse/lib/palette';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import type { SeriesSankeyPointOptionsObject } from 'highcharts';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import type RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import { type EcommerceFunnelMetrics } from './ecommerce-funnel/series-config';

const COLORS = {
  productRecommended: PALETTE['vis-blue-50'],
  productClicked: PALETTE['vis-orange-50'],
  cartCreated: PALETTE['vis-mint-50'],
  checkoutClicked: PALETTE['vis-violet-50'],
};

function stripZeroWeights(
  data: SeriesSankeyPointOptionsObject[],
): SeriesSankeyPointOptionsObject[] {
  return data.filter((point) => point.weight !== 0);
}

interface FunnelPoint extends Point {
  title?: string;
  findByLabel?: string;
  weight?: number;
  count?: number;
  id?: string;
  to?: string;
}

interface Signature {
  Args: {
    renderableChart: RenderableChart;
    openUnderlyingDataModal: (chartSeries: ChartSeries) => void;
  };
}

export default class EcommerceFunnel extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare intercomEventService: any;

  @tracked tooltipOpenerElement: HTMLElement | undefined = undefined;
  @tracked hoveredPoint: FunnelPoint | null = null;

  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.sankeyChartDefinition,
    reportState: () => this.args.renderableChart.reportState,
  });

  get highchartsSeries() {
    return [this.sankeySeries] as SeriesOptionsType[];
  }

  get sankeyChartDefinition() {
    let chartSeries = this.args.renderableChart.chartSeries.map((series: ChartSeries) => ({
      metric_id: series.metricId,
      label: series.label,
      filters: series.filters,
    }));
    return {
      chart_series: chartSeries,
      view_by: null,
    };
  }

  get highchartsOptions(): Options {
    let component = this;
    return {
      xAxis: { visible: false },
      yAxis: { visible: false },
      tooltip: { enabled: false },
      chart: {
        marginRight: 250,
        marginTop: 20,
        animation: false,
      },
      plotOptions: {
        sankey: {
          point: {
            events: {
              mouseOver(this: Point, event: PointerEvent) {
                component.hoveredPoint = this;
                component.tooltipOpenerElement = (event.target as any).graphic.element;
                return false;
              },
              mouseOut(this: Point, _event: PointerEvent) {
                component.tooltipOpenerElement = undefined;
                return false;
              },
              click(this: Point, _event: PointerEvent) {
                component.hoveredPoint = this;
                component.onViewConversations();
                return false;
              },
            },
          },
          cursor: 'pointer',
          nodeWidth: 6,
          borderRadius: 3,
          nodePadding: 20,
          nodeAlignment: 'top',
          linkOpacity: 1.0,
          states: {
            inactive: {
              opacity: 1,
              linkOpacity: 1.0,
            },
          },
          minLinkWidth: 3,
          dataLabels: {
            padding: 0,
            color: 'black',
            useHTML: true,
            crop: false,
            overflow: 'allow',
            allowOverlap: false,
            enabled: true,
            zIndex: 10,
            nodeFormat: `
            <div class="flex flex-col text-default text-caption">
              <div class="flex flex-row font-mono uppercase leading-4">
                {point.title}
              </div>
              <div class="flex flex-row mt-1">
                <div class="p-1 rounded text-data-detail {point.textClass}" style="background-color: {point.color}">
                  {point.sum:,.0f}
                </div>
              </div>
            </div>
          `,
          },
          levels: [
            {
              level: 0,
              dataLabels: { x: 20, align: 'left' },
            },
            {
              level: 1,
              dataLabels: { x: 20, align: 'left' },
            },
            {
              level: 2,
              dataLabels: { x: 20, align: 'left' },
            },
            {
              level: 3,
              dataLabels: {
                x: -20,
                align: 'right',
                nodeFormat: `
                <div class="flex flex-col text-default text-caption">
                  <div class="flex flex-row font-mono uppercase leading-4 justify-end">
                    {point.title}
                  </div>
                  <div class="flex flex-row justify-end mt-1">
                    <div class="p-1 rounded text-data-detail {point.textClass}" style="background-color: {point.color}">
                      {point.sum:,.0f}
                    </div>
                  </div>
                </div>
                `,
              },
            },
          ],
        } as any,
      },
    };
  }

  get metrics(): EcommerceFunnelMetrics {
    let seriesFilters = [
      'ecomInvolved',
      'productRecommended',
      'productRecommendedAndClicked',
      'productRecommendedAndCartCreated',
      'productClickedAndCartCreated',
      'cartCreatedAndCheckout',
      'productClicked',
      'cartCreated',
      'checkoutClicked',
    ] as const;
    let result = {} as EcommerceFunnelMetrics;
    let chartData = this.dataResource.chartData;

    for (let i = 0; i < chartData.length; i++) {
      let key = seriesFilters[i];
      result[key] = chartData[i].data[0];
    }

    return result;
  }

  get hasData() {
    return stripZeroWeights(this.sankeyLinks).length > 0;
  }

  get isLoading() {
    return this.dataResource.isLoading;
  }

  get error() {
    return this.dataResource.error;
  }

  get tooltipValue() {
    if (!this.hoveredPoint) {
      return '';
    }
    let value = this.hoveredPoint.weight || this.hoveredPoint.count || 0;
    return this.dataResource.formatValue({
      value,
      metricId: 'v1.new_conversations',
    });
  }

  get productRecommendedNode() {
    return {
      id: 'productRecommended',
      column: 0,
      title: this.intl.t(
        'ai-agent.analyze.ecommerce-performance.funnel-metrics.productRecommended',
      ),
      count: this.metrics.productRecommended,
      color: COLORS.productRecommended,
      textClass: 'text-on-neutral',
    };
  }

  get productClickedNode() {
    return {
      id: 'productClicked',
      column: 1,
      title: this.intl.t('ai-agent.analyze.ecommerce-performance.funnel-metrics.productClicked'),
      count: this.metrics.productClicked,
      color: COLORS.productClicked,
      textClass: 'text-on-neutral',
    };
  }

  get cartCreatedNode() {
    return {
      id: 'cartCreated',
      column: 2,
      title: this.intl.t('ai-agent.analyze.ecommerce-performance.funnel-metrics.cartCreated'),
      count: this.metrics.cartCreated,
      color: COLORS.cartCreated,
      textClass: 'text-default',
    };
  }

  get checkoutNode() {
    return {
      id: 'checkoutClicked',
      column: 3,
      title: this.intl.t('ai-agent.analyze.ecommerce-performance.funnel-metrics.checkoutClicked'),
      count: this.metrics.checkoutClicked,
      color: COLORS.checkoutClicked,
      textClass: 'text-on-neutral',
    };
  }

  get sankeyLinks(): SeriesSankeyPointOptionsObject[] {
    let m = this.metrics;
    return [
      {
        from: 'productRecommended',
        to: 'cartCreated',
        name: this.intl.t(
          'ai-agent.analyze.ecommerce-performance.funnel-metrics.productRecommendedToCartCreated',
        ),
        weight: m.productRecommendedAndCartCreated,
      },
      {
        from: 'productRecommended',
        to: 'productClicked',
        name: this.intl.t(
          'ai-agent.analyze.ecommerce-performance.funnel-metrics.productRecommendedToProductClicked',
        ),
        weight: m.productRecommendedAndClicked,
      },
      {
        from: 'productClicked',
        to: 'cartCreated',
        name: this.intl.t(
          'ai-agent.analyze.ecommerce-performance.funnel-metrics.productClickedToCartCreated',
        ),
        weight: m.productClickedAndCartCreated,
      },
      {
        from: 'cartCreated',
        to: 'checkoutClicked',
        name: this.intl.t(
          'ai-agent.analyze.ecommerce-performance.funnel-metrics.cartCreatedToCheckoutClicked',
        ),
        weight: m.cartCreatedAndCheckout,
      },
    ];
  }

  get sankeySeries(): SeriesSankeyOptions {
    let allNodes = [
      this.productRecommendedNode,
      this.productClickedNode,
      this.cartCreatedNode,
      this.checkoutNode,
    ];
    let data = stripZeroWeights(this.sankeyLinks);
    let connectedNodeIds = new Set(data.flatMap((d) => [d.from, d.to]));
    let nodes = allNodes.filter((node) => node.count > 0 || connectedNodeIds.has(node.id));
    return {
      type: 'sankey',
      linkColorMode: 'gradient',
      animation: false,
      custom: {
        total: this.metrics.productRecommended,
      },
      nodes,
      data,
    };
  }

  @action
  onViewConversations() {
    if (!this.hoveredPoint) {
      return;
    }

    let metricName =
      this.hoveredPoint.findByLabel || this.hoveredPoint.title || this.hoveredPoint.name;
    let chartSeries = this.args.renderableChart.chartSeries.toArray().findBy('label', metricName);

    if (chartSeries) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'opened',
        object: 'underlying_data_modal',
        chart_metric_id: chartSeries.metric.id,
        chart_id: 'chart-ecommerce-funnel-sankey',
      });
      this.args.openUnderlyingDataModal(chartSeries);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::EcommerceFunnel': typeof EcommerceFunnel;
    'reporting/bespoke/fin-performance/ecommerce-funnel': typeof EcommerceFunnel;
  }
}
